import { createBuyBonusComponent } from '@modules/src/buyBonus/createBuyBonusComponent'
import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '@/containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { ButtonState } from '@modules/src/enums/ButonStates'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { paintViewportCoverOverlay } from '@dragonframework/modules'
import * as PIXI from 'pixi.js'
import {
    BUY_BONUS_CAPTION_KEYS,
    BuyBonusConfirmLabel,
    BuyBonusValueField,
    createBuyBonusCaption,
    renderBuyBonusCaption,
    renderBuyBonusValue,
    getCurrentCoinBetAmount,
    getCurrentBuyBonusTotalAmount,
    canAffordBuyBonus,
    syncBuyBonusMinusButton,
    syncBuyBonusPlusButton,
    syncBuyBonusConfirmButton,
    enableBuyBonusButton,
    captureBuyBonusLabel,
    initBuyBonusConfirmButton,
    applyBuyBonusConfirmHitArea,
    BUY_BONUS_BET_VALUE_MAX_WIDTH,
    BUY_BONUS_TOTAL_VALUE_MAX_WIDTH,
    syncBuyBonusInsufficientBalance,
    hideBuyBonusInsufficientBalance,
} from '@/managers/ui/BuyBonusManager'
import { BUY_BONUS_INSUFFICIENT_STYLE } from '@/containers/common/TextStyles'
import { isEnglishArtLocale, isSocialCasinoLocale } from '@/utils/Locale'

const BUY_BONUS_OVERLAY_COLOR = 0x01102a

// Shared with hooks.onBetChange below — the engine only calls that hook from
// "Buy Bonus Bet Value Digits"'s default event, which we replace to use BuyBonusManager's renderer.
const onBuyBonusBetChange = (amount: number): void => {
    console.log('[BuyBonus] bet changed:', amount)
}

// Buy Bonus popup background spine — asset name + animation names in one place.
// Registered in AssetsConfig.json as "buy_bonus" -> animations/buybonus/buy_bonus.json
const BUY_BONUS_SPINE_ASSET = 'buy_bonus'
const BUY_BONUS_SPINE_ANIM_IN = 'in'
const BUY_BONUS_SPINE_ANIM_LOOP = 'loop'
const BUY_BONUS_SPINE_SKIN = isSocialCasinoLocale() ? 'PLAY' : 'BUY'

//## Where the popup puts the animation. Shared with the captions below, which are placed off the
//## skeleton's own attachment coordinates and so have to follow the same origin and scale.
const BUY_BONUS_SPINE_PLACEMENT = {
    [Orientation.LANDSCAPE]: { x: 523, y: 482, scale: 0.75 },
    [Orientation.PORTRAIT]: { x: 500, y: 500, scale: 0.8 },
} as const

/**
 * Centres of the "Bet per spin" and "Total Bet" attachments in the skeleton's own space, read off
 * buy_bonus.json's BUY and PLAY skins. Spine's y axis points up, hence the sign flip below.
 */
const BUY_BONUS_CAPTION_ATTACHMENTS = {
    betPerSpin: { x: -5.97, y: -149.94 },
    totalBet: { x: 5.51, y: -489.99 },
} as const

const BUY_BONUS_SPINE_ORIENTATIONS = [Orientation.LANDSCAPE, Orientation.PORTRAIT] as const

const spinePlacementPosition = () =>
    BUY_BONUS_SPINE_ORIENTATIONS.map((orientation) => ({
        orientation,
        x: BUY_BONUS_SPINE_PLACEMENT[orientation].x,
        y: BUY_BONUS_SPINE_PLACEMENT[orientation].y,
    }))

const spinePlacementScale = () =>
    BUY_BONUS_SPINE_ORIENTATIONS.map((orientation) => ({
        orientation,
        x: BUY_BONUS_SPINE_PLACEMENT[orientation].scale,
        y: BUY_BONUS_SPINE_PLACEMENT[orientation].scale,
    }))

const spineAttachmentPosition = (attachment: { x: number; y: number }) =>
    BUY_BONUS_SPINE_ORIENTATIONS.map((orientation) => {
        const { x, y, scale } = BUY_BONUS_SPINE_PLACEMENT[orientation]

        return {
            orientation,
            x: x + attachment.x * scale,
            y: y - attachment.y * scale,
        }
    })

//## The BUY and PLAY skins letter "BET PER SPIN" and "TOTAL BET" into the message box, so outside the
//## locales that art covers the game types those captions itself and the drawn ones have to go. The
//## rig ships `in_ml`/`loop_ml` clips that strip them, but those also strip the title, whose animated
//## glow has no typed equivalent — clearing the two slots leaves everything else as authored.
//## `start()` puts them back (it re-applies the skin, which restores each slot's setup attachment),
//## so this runs after every call to it.
const BUY_BONUS_SPINE_CAPTION_SLOTS = ['Bet per spin', 'Total Bet']

const hideBuyBonusSpineCaptions = (spine: SpineAnimation): void => {
    if (isEnglishArtLocale()) {
        return
    }

    const skeleton = (spine.animation as any)?.skeleton
    BUY_BONUS_SPINE_CAPTION_SLOTS.forEach((slot) => skeleton?.findSlot?.(slot)?.setAttachment(null))
}

const buyBonusCaptionItem = (
    name: string,
    translationKey: string,
    attachment: { x: number; y: number }
): IComponent =>
    ({
        name,
        type: AssetType.CUSTOM,
        elementFactory: createBuyBonusCaption,
        visible: false,
        position: spineAttachmentPosition(attachment),
        scale: spinePlacementScale(),
        events: [
            {
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [GameSignals.openBuyBonusPopup, GameSignals.buyBonusIntroComplete],
                callback: (data: ICustomEventData) => renderBuyBonusCaption(data, translationKey),
            },
            {
                event: ComponentEvent.SHOW,
                onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
            },
            {
                event: ComponentEvent.HIDE,
                onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
            },
        ],
    }) as IComponent

const BUY_BONUS_CAPTION_ITEMS: IComponent[] = isEnglishArtLocale()
    ? []
    : [
          buyBonusCaptionItem(
              'Buy Bonus Bet Per Spin Caption',
              BUY_BONUS_CAPTION_KEYS.betPerSpin,
              BUY_BONUS_CAPTION_ATTACHMENTS.betPerSpin
          ),
          buyBonusCaptionItem(
              'Buy Bonus Total Bet Caption',
              BUY_BONUS_CAPTION_KEYS.totalBet,
              BUY_BONUS_CAPTION_ATTACHMENTS.totalBet
          ),
      ]

//## Reveal Minus/Plus/Buy/Close once "in" is this far through its own duration, instead of
//## waiting for full completion — keeps buttons in sync even if the clip's length changes later.
const BUY_BONUS_INTRO_REVEAL_FRACTION = 0.5

let buyBonusIntroRevealTimer: ReturnType<typeof setTimeout> | null = null

const cancelBuyBonusIntroReveal = (): void => {
    if (buyBonusIntroRevealTimer === null) return

    clearTimeout(buyBonusIntroRevealTimer)
    buyBonusIntroRevealTimer = null
}

const isBuyBonusPopupAllowedNow = (): boolean => {
    if (Boolean((Facade as any).isBigWinPresentationActive)) {
        return false
    }

    if (Boolean((Facade as any).isFsCollectPresentationActive)) {
        return false
    }

    if (Boolean(Facade.model?.isBuyBonusPending)) {
        return true
    }

    return Facade.currentState === GameStatesEnum.IDLE_STATE
}

export const BUY_BONUS_COMPONENT: IComponent = createBuyBonusComponent({
    containerName: ContainersNames.BUY_BONUS_CONTAINER,

    // EXAMPLE 1: Custom asset naming convention
    assets: {
        assets: {
            minusNormal: 'minus_button_normal.png',
            minusHover: 'minus_button_hover.png',
            minusDown: 'minus_button_down.png',
            plusNormal: 'plus_button_normal.png',
            plusHover: 'plus_button_hover.png',
            plusDown: 'plus_button_down.png',
        },
    },

    // EXAMPLE 2: Patch existing elements
    itemPatches: [
        {
            targetName: 'Buy Bonus Popup Content',
            mode: 'merge',
            patch: {
                position: [
                    { orientation: Orientation.LANDSCAPE, x: 604, y: 51.5 },
                    { orientation: Orientation.PORTRAIT, x: 38, y: 400 },
                ],
                scale: [
                    { orientation: Orientation.LANDSCAPE, x: 0.8, y: 0.8 },
                    { orientation: Orientation.PORTRAIT, x: 1, y: 1 },
                ],
            },
        },
        {
            targetName: 'Buy Bonus Overlay',
            mode: 'merge',
            patch: {
                componentStyle: {
                    color: BUY_BONUS_OVERLAY_COLOR,
                },
                scale: [
                    { orientation: Orientation.LANDSCAPE, x: 2, y: 2 },
                    { orientation: Orientation.PORTRAIT, x: 2, y: 2 },
                ],
                events: [
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [GameSignals.openBuyBonusPopup, ModuleSignals.gameResized],
                        callback: (data: ICustomEventData) => {
                            paintViewportCoverOverlay(
                                data.component.componentElement as PIXI.Graphics,
                                BUY_BONUS_OVERLAY_COLOR,
                                0.87
                            )
                        },
                    },
                ],
            },
        },
        {
            targetName: 'Buy Bonus Popup Background',
            mode: 'replace',
            patch: {
                name: 'Buy Bonus Popup Background',
                type: AssetType.SPINE,
                src: BUY_BONUS_SPINE_ASSET,
                animationSettings: {
                    default: {
                        animNames: [BUY_BONUS_SPINE_ANIM_IN],
                        loops: [false],
                        track: 0,
                        timeScales: [0],
                    },
                },
                events: [
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [GameSignals.openBuyBonusPopup],
                        callback: (data: ICustomEventData) => {
                            const spine: SpineAnimation = data.component.componentElement
                            spine.visible = false
                            spine.setSkin(BUY_BONUS_SPINE_SKIN)

                            spine.start([BUY_BONUS_SPINE_ANIM_IN], [false], 0, [1], () => {
                                spine.start([BUY_BONUS_SPINE_ANIM_LOOP], [true], 0, [1])
                                hideBuyBonusSpineCaptions(spine)
                            })
                            hideBuyBonusSpineCaptions(spine)
                            requestAnimationFrame(() => {
                                spine.visible = true
                            })

                            const introDurationSeconds = spine.animation?.state?.tracks?.[0]?.animation?.duration || 0
                            const revealDelayMs = introDurationSeconds * 1000 * BUY_BONUS_INTRO_REVEAL_FRACTION

                            cancelBuyBonusIntroReveal()
                            buyBonusIntroRevealTimer = setTimeout(() => {
                                buyBonusIntroRevealTimer = null
                                GameSignals.buyBonusIntroComplete.emit()
                            }, revealDelayMs)
                        },
                    },
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                        callback: (data: ICustomEventData) => {
                            const spine: SpineAnimation = data.component.componentElement
                            spine.animation?.state?.clearTracks()
                            cancelBuyBonusIntroReveal()
                        },
                    },
                ],
                scale: spinePlacementScale(),
                position: spinePlacementPosition(),
            },
        },
        {
            targetName: 'Buy Bonus Title',
            mode: 'merge',
            patch: {
                visible: false,
            },
        },
        {
            targetName: 'Buy Bonus Bet Per Spin Label',
            mode: 'merge',
            patch: {
                visible: false,
            },
        },
        {
            targetName: 'Buy Bonus Total Bet Label',
            mode: 'merge',
            patch: {
                visible: false,
            },
        },
        {
            targetName: 'Buy Bonus Bet Frame',
            mode: 'merge',
            patch: {
                visible: false,
            },
        },
        {
            targetName: 'Buy Bonus Bet Frame 2',
            mode: 'merge',
            patch: {
                visible: false,
            },
        },
        {
            targetName: 'Buy Bonus Minus',
            mode: 'merge',
            patch: {
                visible: false,
                position: [
                    { orientation: Orientation.LANDSCAPE, x: 195, y: 407 },
                    { orientation: Orientation.PORTRAIT, x: 158, y: 424 },
                ],
                scale: [
                    {
                        orientation: Orientation.LANDSCAPE,
                        x: 0.75,
                        y: 0.75,
                    },
                    {
                        orientation: Orientation.PORTRAIT,
                        x: 0.75,
                        y: 0.75,
                    },
                ],
                events: [
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [
                            GameSignals.openBuyBonusPopup,
                            GameSignals.betHasChanged,
                            GameSignals.buyBonusIntroComplete,
                        ],
                        callback: syncBuyBonusMinusButton,
                    },
                    {
                        event: ComponentEvent.SHOW,
                        onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
                    },
                    {
                        event: ComponentEvent.HIDE,
                        onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                    },
                ],
            },
        },
        {
            targetName: 'Buy Bonus Plus',
            mode: 'merge',
            patch: {
                visible: false,
                position: [
                    { orientation: Orientation.LANDSCAPE, x: 706, y: 410 },
                    { orientation: Orientation.PORTRAIT, x: 697, y: 428 },
                ],
                scale: [
                    {
                        orientation: Orientation.LANDSCAPE,
                        x: 0.75,
                        y: 0.75,
                    },
                    {
                        orientation: Orientation.PORTRAIT,
                        x: 0.75,
                        y: 0.75,
                    },
                ],
                events: [
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [
                            GameSignals.openBuyBonusPopup,
                            GameSignals.betHasChanged,
                            GameSignals.buyBonusIntroComplete,
                        ],
                        callback: syncBuyBonusPlusButton,
                    },
                    {
                        event: ComponentEvent.SHOW,
                        onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
                    },
                    {
                        event: ComponentEvent.HIDE,
                        onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                    },
                ],
            },
        },
        {
            targetName: 'Buy Bonus Bet Value Digits',
            mode: 'merge',
            patch: {
                visible: false,
                type: AssetType.CUSTOM,
                elementFactory: () => new BuyBonusValueField(BUY_BONUS_BET_VALUE_MAX_WIDTH),
                position: [
                    { orientation: Orientation.LANDSCAPE, x: 528, y: 473 },
                    { orientation: Orientation.PORTRAIT, x: 505, y: 490 },
                ],
                scale: [
                    {
                        orientation: Orientation.LANDSCAPE,
                        x: 1,
                        y: 1,
                    },
                    {
                        orientation: Orientation.PORTRAIT,
                        x: 1,
                        y: 1,
                    },
                ],
                events: [
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [GameSignals.openBuyBonusPopup, GameSignals.betHasChanged],
                        callback: (data: ICustomEventData) => {
                            const amount = getCurrentCoinBetAmount()
                            renderBuyBonusValue(data.component.componentElement, amount)
                            onBuyBonusBetChange(amount)
                        },
                    },
                    {
                        event: ComponentEvent.SHOW,
                        onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
                    },
                    {
                        event: ComponentEvent.HIDE,
                        onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                    },
                ],
            },
        },
        {
            targetName: 'Buy Bonus Total Value Digits',
            mode: 'merge',
            patch: {
                visible: false,
                type: AssetType.CUSTOM,
                elementFactory: () => new BuyBonusValueField(BUY_BONUS_TOTAL_VALUE_MAX_WIDTH),
                position: [
                    { orientation: Orientation.LANDSCAPE, x: 524, y: 728 },
                    { orientation: Orientation.PORTRAIT, x: 501, y: 763 },
                ],
                scale: [
                    {
                        orientation: Orientation.LANDSCAPE,
                        x: 1,
                        y: 1,
                    },
                    {
                        orientation: Orientation.PORTRAIT,
                        x: 1,
                        y: 1,
                    },
                ],
                events: [
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [GameSignals.openBuyBonusPopup, GameSignals.betHasChanged],
                        callback: (data: ICustomEventData) => {
                            renderBuyBonusValue(data.component.componentElement, getCurrentBuyBonusTotalAmount())
                        },
                    },
                    {
                        event: ComponentEvent.SHOW,
                        onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
                    },
                    {
                        event: ComponentEvent.HIDE,
                        onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                    },
                ],
            },
        },
        {
            targetName: 'Buy Bonus Button',
            mode: 'merge',
            patch: {
                visible: false,
                position: [
                    { orientation: Orientation.LANDSCAPE, x: 295, y: 925 },
                    { orientation: Orientation.PORTRAIT, x: 270, y: 980 },
                ],
                scale: [
                    { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                    { orientation: Orientation.PORTRAIT, x: 0.7, y: 0.7 },
                ],
                events: [
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [GameSignals.openBuyBonusPopup, GameSignals.buyBonusIntroComplete],
                        callback: initBuyBonusConfirmButton,
                    },
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [
                            GameSignals.betHasChanged,
                            GameSignals.balanceHasChanged,
                            GameSignals.buyBonusIntroComplete,
                        ],
                        callback: syncBuyBonusConfirmButton,
                    },
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [
                            GameSignals.openBuyBonusPopup,
                            GameSignals.buyBonusIntroComplete,
                            ModuleSignals.gameResized,
                        ],
                        callback: applyBuyBonusConfirmHitArea,
                    },
                    {
                        event: ComponentEvent.SHOW,
                        onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
                    },
                    {
                        event: ComponentEvent.HIDE,
                        onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                    },
                ],
            },
        },
        {
            targetName: 'Buy Bonus Button Label',
            mode: 'merge',
            patch: {
                visible: false,
                //## Typed rather than the atlas' English-only "BUY" art — see BuyBonusConfirmLabel.
                type: AssetType.CUSTOM,
                elementFactory: () => new BuyBonusConfirmLabel(),
                position: [
                    { orientation: Orientation.LANDSCAPE, x: 405, y: 975 },
                    { orientation: Orientation.PORTRAIT, x: 380, y: 1030 },
                ],
                scale: [
                    { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                    { orientation: Orientation.PORTRAIT, x: 0.7, y: 0.7 },
                ],
                events: [
                    //## Hand the element to BuyBonusManager so the Buy button can swap it on hover.
                    {
                        event: ComponentEvent.CUSTOM_NEW,
                        onGameSignalsTriggered: [
                            GameSignals.openBuyBonusPopup,
                            GameSignals.betHasChanged,
                            GameSignals.balanceHasChanged,
                            GameSignals.buyBonusIntroComplete,
                        ],
                        callback: captureBuyBonusLabel,
                    },
                    {
                        event: ComponentEvent.SHOW,
                        onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
                    },
                    {
                        event: ComponentEvent.HIDE,
                        onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                    },
                ],
            },
        },
    ],

    behavior: {
        onConfirmClick: () => {
            if (!canAffordBuyBonus()) {
                return
            }
            //## SFX first, before anything below it. buyBonusClicked drives the state machine
            //## synchronously into START_ROUND_STATE and hidePopup tears the popup down; when
            //## this was emitted from hooks.onConfirm (which core calls after onConfirmClick)
            //## the sound landed audibly late behind all of that work.
            ;(GameSignals as any).buyBonusConfirmButtonClicked?.emit()

            // Arm before emit/hide so panel unlock-on-close cannot re-enable stake/settings.
            if (Facade.model) {
                Facade.model.isBuyBonusPending = true
            }
            GameSignals.buyBonusClicked.emit(1)
            GameSignals.hidePopup.emit()
        },
    },

    hooks: {
        onOpen: () => {
            // Close immediately if opened mid-spin / during commendation (ghost hit or race after await).
            if (!isBuyBonusPopupAllowedNow()) {
                ;(Facade as any).isBuyBonusPopupOpen = false
                Facade.isPopupBlockingInput = false
                GameSignals.hidePopup.emit()
                return
            }

            Facade.isPopupBlockingInput = true
            ;(Facade as any).isBuyBonusPopupOpen = true
            GameSignals.lockGamePanel.emit()
        },
        // Called when the popup closes
        onClose: () => {
            console.log('[BuyBonus] popup closed')
            ;(Facade as any).isBuyBonusPopupOpen = false
            if (!Facade.model?.isBuyBonusPending) {
                Facade.isPopupBlockingInput = false
            }
            // Always unlock the spin button path; stake/settings/autoplay stay restricted
            // via isBuyBonusPending / isRoundRunning while the purchase spin runs.
            GameSignals.unLockGamePanel.emit()
        },
        // Note: no longer invoked by the engine (see onBuyBonusBetChange above), kept for
        // interface completeness in case this item's itemPatch is ever removed.
        onBetChange: onBuyBonusBetChange,
        // Called after the player clicks confirm/buy
        onConfirm: () => {
            if (!canAffordBuyBonus()) {
                return
            }
            console.log('[BuyBonus] player confirmed buy')
            if (Facade.model) {
                Facade.model.isBuyBonusPending = true
            }
            //## buyBonusConfirmButtonClicked is emitted from behavior.onConfirmClick instead —
            //## core runs that first, so the SFX is not delayed behind the round-start work.
        },
        //     additionalSignalsOnOpen: ['trackBuyBonusImpression'],
        //     additionalSignalsOnConfirm: ['trackBuyBonusPurchase'],
    },

    // EXAMPLE 4: Append custom element(s) into popup
    appendItems: [
        {
            parentName: 'Buy Bonus Popup Content',
            items: [
                ...BUY_BONUS_CAPTION_ITEMS,
                {
                    name: 'Buy Bonus Insufficient Balance',
                    type: AssetType.TEXT,
                    translationKey: '',
                    visible: false,
                    textStyle: [
                        {
                            orientation: Orientation.ANY,
                            options: { ...BUY_BONUS_INSUFFICIENT_STYLE },
                        },
                    ],
                    position: [
                        { orientation: Orientation.LANDSCAPE, x: 539, y: 375 },
                        { orientation: Orientation.PORTRAIT, x: 514, y: 386 },
                    ],
                    events: [
                        {
                            event: ComponentEvent.CUSTOM_NEW,
                            onGameSignalsTriggered: [
                                GameSignals.betHasChanged,
                                GameSignals.balanceHasChanged,
                                GameSignals.buyBonusIntroComplete,
                            ],
                            callback: syncBuyBonusInsufficientBalance,
                        },
                        {
                            event: ComponentEvent.CUSTOM_NEW,
                            onGameSignalsTriggered: [
                                GameSignals.openBuyBonusPopup,
                                GameSignals.hidePopup,
                                GameSignals.buyBonusResponse,
                            ],
                            callback: hideBuyBonusInsufficientBalance,
                        },
                    ],
                },
                {
                    name: 'Buy Bonus Close Button',
                    type: AssetType.BUTTON,
                    src: 'close_button_normal.png',
                    anchor: 0.5,
                    visible: false,
                    position: [
                        { orientation: Orientation.LANDSCAPE, x: 841, y: -1.5 },
                        { orientation: Orientation.PORTRAIT, x: 870, y: -15 },
                    ],
                    scale: [
                        { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                        { orientation: Orientation.PORTRAIT, x: 0.6, y: 0.6 },
                    ],
                    events: [
                        {
                            event: ComponentEvent.CUSTOM_NEW,
                            onGameSignalsTriggered: [
                                GameSignals.openBuyBonusPopup,
                                GameSignals.buyBonusIntroComplete,
                            ],
                            callback: enableBuyBonusButton,
                        },
                        {
                            event: ComponentEvent.SHOW,
                            onGameSignalsTriggered: [GameSignals.buyBonusIntroComplete],
                        },
                        {
                            event: ComponentEvent.HIDE,
                            onGameSignalsTriggered: [GameSignals.hidePopup, GameSignals.buyBonusResponse],
                        },
                    ],
                    buttonStates: [
                        {
                            state: ButtonState.POINTER_UP_OUT,
                            src: 'close_button_normal.png',
                            events: [],
                        },
                        {
                            state: ButtonState.POINTER_OUT,
                            src: 'close_button_normal.png',
                            events: [],
                        },
                        {
                            state: ButtonState.POINTER_OVER,
                            src: 'close_button_hover.png',
                            events: [],
                        },
                        {
                            state: ButtonState.POINTER_DOWN,
                            src: 'close_button_down.png',
                            events: [],
                        },
                        {
                            state: ButtonState.POINTER_UP,
                            src: 'close_button_normal.png',
                            events: [
                                {
                                    event: ComponentEvent.TRIGGER_SIGNAL,
                                    signalsToTrigger: [GameSignals.playUIGenSound, GameSignals.hidePopup],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
})
